import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import appConfig from './config/appConfig';
import './output.css';

// SVG icons as components
const UserIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const LockIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const EyeOffIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
  </svg>
);

const UpdateUserAccount = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [formData, setFormData] = useState({
    user_name: '',
    password: '',
    confirmPassword: '',
    access_level: 9,
    default_project: '',
    user_status: 'Active',
    user_role: '',
    business_team_id: ''
  });

  // Additional state for dropdowns
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [businessTeams, setBusinessTeams] = useState([]);
  const [loadingBusinessTeams, setLoadingBusinessTeams] = useState(false);
  const [userRoles, setUserRoles] = useState([]);
  const [loadingUserRoles, setLoadingUserRoles] = useState(false);

  const navigate = useNavigate();

  // Get authentication data from localStorage
  const user_id = localStorage.getItem('user_id');
  const corporate_account = localStorage.getItem('corporate_account');
  const login_token = localStorage.getItem('authToken');

  // Fetch projects when component mounts
  const fetchProjects = async () => {
    if (!corporate_account || !login_token) return;

    setLoadingProjects(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_project_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporate_account
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setProjects(data.project_list || []);
      } else {
        console.error('Failed to fetch projects:', data.status_description);
        setProjects([]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Fetch business teams for the selected project
  const fetchBusinessTeams = async (projectId) => {
    if (!projectId || !corporate_account || !login_token) return;

    setLoadingBusinessTeams(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_business_team_list'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporate_account,
          project_id: projectId
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setBusinessTeams(data.business_team_list || []);
      } else {
        console.error('Failed to fetch business teams:', data.status_description);
        setBusinessTeams([]);
      }
    } catch (error) {
      console.error('Error fetching business teams:', error);
      setBusinessTeams([]);
    } finally {
      setLoadingBusinessTeams(false);
    }
  };

  // Fetch user roles
  const fetchUserRoles = async (projectId = null) => {
    if (!corporate_account || !login_token) return;

    setLoadingUserRoles(true);
    try {
      const response = await fetch(appConfig.getUrl('/get_statuses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporate_account,
          project_id: projectId || "ALL PROJECTS",
          entity: "USER ROLES"
        })
      });

      const data = await response.json();
      if (data.status === 'Success') {
        setUserRoles(data.entity_statuses || []);
      } else {
        console.error('Failed to fetch user roles:', data.status_description);
        setUserRoles([]);
      }
    } catch (error) {
      console.error('Error fetching user roles:', error);
      setUserRoles([]);
    } finally {
      setLoadingUserRoles(false);
    }
  };

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!user_id || !login_token) {
      navigate('/LoginAndAuthentication');
      return;
    }
  }, [user_id, login_token, navigate]);

  // Fetch initial data when component mounts
  useEffect(() => {
    if (corporate_account && login_token) {
      fetchProjects();
      fetchUserRoles();
    }
  }, [corporate_account, login_token]);

  // Fetch user profile data on component mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user_id || !login_token) return;

      try {
        setIsLoadingProfile(true);
        const response = await fetch(appConfig.getUrl('/get_user_info'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${login_token}`
          },
          body: JSON.stringify({
            user_id: user_id
          })
        });

        const data = await response.json();

        if (data.status !== 'Success') {
          throw new Error(data.status_description || 'Failed to fetch user profile');
        }

        // Populate form with existing user data
        setFormData({
          user_name: data.user_name || '',
          password: '', // Always start with empty password
          confirmPassword: '', // Always start with empty confirm password
          access_level: data.access_level || 9,
          default_project: data.default_project || '',
          user_status: data.user_status || 'Active',
          user_role: data.user_role || '',
          business_team_id: data.business_team_id || ''
        });

        // If there's a default project, fetch business teams for it
        if (data.default_project) {
          fetchBusinessTeams(data.default_project);
          fetchUserRoles(data.default_project);
        }

      } catch (err) {
        setError(err.message || 'Failed to load user profile');
        console.error('Error fetching user profile:', err);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    fetchUserProfile();
  }, [user_id, login_token]);

  const validatePassword = (password) => {
    if (!password) return true; // Empty password means no change
    return password.length >= 8 &&
           /[A-Z]/.test(password) &&
           /[a-z]/.test(password) &&
           /[0-9]/.test(password) &&
           /[^A-Za-z0-9]/.test(password);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let updatedFormData = { ...formData, [name]: value };

    // Handle default project change
    if (name === 'default_project') {
      // Reset business team when project changes
      updatedFormData.business_team_id = '';

      // Fetch business teams and user roles for the selected project
      if (value) {
        fetchBusinessTeams(value);
        fetchUserRoles(value);
      } else {
        setBusinessTeams([]);
        fetchUserRoles(); // Fetch general user roles
      }
    }

    setFormData(updatedFormData);
    setError('');
    setSuccess('');
  };

  const updateUserProfile = async (updateData) => {
    try {
      const requestBody = {
        corporate_account: corporate_account,
        user_id: user_id,
        user_name: updateData.user_name,
        password: updateData.password,
        access_level: updateData.access_level,
        default_project: updateData.default_project,
        business_team_id: updateData.business_team_id,
        user_status: updateData.user_status,
        user_role: updateData.user_role
      };

      const response = await fetch(appConfig.getUrl('/update_user'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.status !== 'Success') {
        throw new Error(data.status_description || 'User update failed');
      }

      return data;
    } catch (err) {
      throw new Error(err.message || 'User update failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      // Validate required fields
      if (!formData.user_name.trim()) {
        throw new Error('Please enter your full name');
      }

      // Validate password only if it's provided
      if (formData.password && !validatePassword(formData.password)) {
        throw new Error('Password must be at least 8 characters long and contain uppercase, lowercase, number, and special character');
      }

      // Validate password confirmation if password is provided
      if (formData.password) {
        if (!formData.confirmPassword) {
          throw new Error('Please confirm your password');
        }
        if (formData.password !== formData.confirmPassword) {
          throw new Error('Passwords do not match');
        }
      }

      // Update user profile
      await updateUserProfile(formData);

      // Update localStorage with new user name
      localStorage.setItem('user_name', formData.user_name);

      setSuccess('Profile updated successfully!');

      // Clear password fields after successful update
      setFormData(prev => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }));

    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/RequirementsPortal');
  };

  if (isLoadingProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="flex items-center justify-center">
          <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="ml-2 text-gray-600">Loading profile...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="p-6">
            {/* Header */}
            <div className="flex justify-center mb-2">
              <img
                src="/logos/logo-reqsharp-blue.svg"
                alt="ReqSharp Logo"
                className="h-16 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>

            <h2 className="text-2xl font-bold text-center mb-6">
              Update User Account
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* User Information Section */}
              <div className="space-y-4 border-b pb-4">
                <h3 className="text-lg font-medium text-gray-900">User Information</h3>

                {/* Email (read-only) */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Email (cannot be changed)</label>
                  <div className="relative">
                    <input
                      type="email"
                      value={user_id}
                      disabled
                      className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                    />
                  </div>
                </div>

                {/* User Name */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Full Name *</label>
                  <div className="relative">
                    <div className="absolute left-3 top-2.5">
                      <UserIcon />
                    </div>
                    <input
                      type="text"
                      name="user_name"
                      placeholder="John Doe"
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.user_name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    New Password (leave blank to keep current password)
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-2.5">
                      <LockIcon />
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      autoComplete="new-password"
                      placeholder="••••••••"
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.password}
                      onChange={handleInputChange}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-2.5"
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                  {formData.password && (
                    <p className="text-xs text-gray-500">
                      Password must be at least 8 characters with uppercase, lowercase, number, and special character.
                    </p>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Confirm New Password {formData.password && <span className="text-red-600">*</span>}
                  </label>
                  <div className="relative">
                    <div className="absolute left-3 top-2.5">
                      <LockIcon />
                    </div>
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      autoComplete="new-password"
                      placeholder="••••••••"
                      className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      disabled={!formData.password}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-2.5"
                      disabled={!formData.password}
                    >
                      {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  </div>
                  {formData.password && (
                    <p className="text-xs text-gray-500">
                      Please confirm your new password.
                    </p>
                  )}
                </div>
              </div>

              {/* Account Settings Section */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Account Settings</h3>

                {/* User Status */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">User Status</label>
                  <select
                    name="user_status"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                    value={formData.user_status}
                    disabled
                    onChange={handleInputChange}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                {/* Access Level */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Access Level</label>
                  <select
                    name="access_level"
                    value={formData.access_level}
                    onChange={handleInputChange}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed"
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(level => (
                      <option key={level} value={level}>
                        {level}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500">
                    Access level is typically managed by system administrators
                  </p>
                </div>

                {/* Default Project */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Default Project</label>
                  {loadingProjects ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 flex items-center">
                      <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading projects...
                    </div>
                  ) : (
                    <select
                      name="default_project"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.default_project}
                      onChange={handleInputChange}
                    >
                      <option value="">Select a project (optional)</option>
                      {projects.map(project => (
                        <option key={project.project_id} value={project.project_id}>
                          {project.project_description} ({project.project_id})
                        </option>
                      ))}
                    </select>
                  )}
                  {projects.length === 0 && !loadingProjects && (
                    <p className="text-xs text-gray-500">
                      No projects available
                    </p>
                  )}
                </div>

                {/* Business Team */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Business Team</label>
                  {loadingBusinessTeams ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 flex items-center">
                      <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading business teams...
                    </div>
                  ) : (
                    <select
                      name="business_team_id"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.business_team_id}
                      onChange={handleInputChange}
                      disabled={!formData.default_project}
                    >
                      <option value="">Select a business team (optional)</option>
                      {businessTeams
                        .filter(team => team.business_team_status === 'Active')
                        .map(team => (
                          <option key={team.business_team_id} value={team.business_team_id}>
                            {team.business_team_description}
                          </option>
                        ))
                      }
                    </select>
                  )}
                  {!formData.default_project && (
                    <p className="text-xs text-gray-500">
                      Please select a default project first to load business teams
                    </p>
                  )}
                  {businessTeams.length === 0 && !loadingBusinessTeams && formData.default_project && (
                    <p className="text-xs text-gray-500">
                      No business teams available for this project
                    </p>
                  )}
                </div>

                {/* User Role */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">User Role</label>
                  {loadingUserRoles ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 flex items-center">
                      <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Loading user roles...
                    </div>
                  ) : (
                    <select
                      name="user_role"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.user_role}
                      onChange={handleInputChange}
                    >
                      <option value="">Select a user role (optional)</option>
                      {userRoles.map(role => (
                        <option key={role.status} value={role.status}>
                          {role.status}
                        </option>
                      ))}
                    </select>
                  )}
                  {userRoles.length === 0 && !loadingUserRoles && (
                    <p className="text-xs text-gray-500">
                      No user roles available
                    </p>
                  )}
                </div>
              </div>

              {/* Error and Success Messages */}
              {error && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              {success && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-600">{success}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-4 pt-4">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex-1 bg-blue-800 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Updating...
                    </span>
                  ) : (
                    'Update Profile'
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleCancel}
                  className="flex-1 bg-yellow-200 py-2 px-4 rounded-md hover:bg-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2"
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateUserAccount;